/**
 * Phase 4: Universal Workflow Engine
 * Schema-driven step-by-step workflow UI for all immigration pathways.
 * Loads workflow schema from backend and renders forms dynamically.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import DocumentPanel from './DocumentPanel';
import FormExportPanel from './FormExportPanel';
import CaseStatusPanel from './CaseStatusPanel';
import { BetaBadge } from './ComingSoon';
import { PurchaseNavButton, usePathwayPurchase } from './CheckoutFlow';
import { getPathway, setPendingPurchase } from '../lib/pathways';
import { API_BASE as API } from '../lib/apiBase';

// ---------------------------------------------------------------------------
// Country list for select dropdowns with empty options
// ---------------------------------------------------------------------------
const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia","Austria",
  "Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan",
  "Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia",
  "Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo (DRC)","Congo (Republic)",
  "Costa Rica","Croatia","Cuba","Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic","Ecuador",
  "Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France",
  "Gabon","Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau",
  "Guyana","Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland",
  "Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kosovo","Kuwait",
  "Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania","Luxembourg",
  "Madagascar","Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius","Mexico",
  "Micronesia","Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nauru",
  "Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway","Oman",
  "Pakistan","Palau","Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal",
  "Qatar","Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines",
  "Samoa","San Marino","Sao Tome and Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore",
  "Slovakia","Slovenia","Solomon Islands","Somalia","South Africa","South Korea","South Sudan","Spain","Sri Lanka","Sudan",
  "Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand","Timor-Leste","Togo",
  "Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine","United Arab Emirates",
  "United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu","Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
].map(c => ({ value: c, label: c }));

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.detail?.message || data.detail || 'Request failed'), { data, status: res.status });
  return data;
};

// ---------------------------------------------------------------------------
// Case Strength Meter
// ---------------------------------------------------------------------------
const CaseStrengthMeter = ({ score, level, recommendations }) => {
  if (score === null || score === undefined) {
    return (
      <div className="text-center text-gray-500 text-sm py-2">
        <i className="fas fa-chart-bar mr-2"></i>
        Complete steps to see your case strength
      </div>
    );
  }

  const color = level === 'strong' ? '#16a34a' : level === 'moderate' ? '#ca8a04' : '#dc2626';
  const label = level === 'strong' ? 'Strong' : level === 'moderate' ? 'Moderate' : 'Needs Attention';
  const recs = recommendations || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          Case Strength <BetaBadge />
        </span>
        <span className="text-sm font-bold" style={{ color }}>{label}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div
          className="h-3 rounded-full transition-all duration-700"
          style={{ width: `${score}%`, backgroundColor: color }}
        ></div>
      </div>
      <div className="text-right text-xs text-gray-500 mt-1">{score}/100</div>
      {recs.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {recs.slice(0, 3).map((r, i) => (
            <li key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <i className="fas fa-lightbulb mr-1.5 text-amber-500"></i>{r}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Field Renderer — renders a single schema field
// ---------------------------------------------------------------------------
const FieldRenderer = ({ field, value, onChange, disabled }) => {
  const { field_id, field_type, label, required, help_text, validation_rules } = field;
  // Auto-populate country selects that have empty options
  const options = (field.options && field.options.length > 0)
    ? field.options
    : (field_type === 'select' && field_id.toLowerCase().includes('country') ? COUNTRIES : field.options);
  const baseClass = `w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : 'bg-white border-gray-300'}`;

  const handleChange = (e) => {
    const val = field_type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;
    onChange(field_id, val);
  };

  return (
    <div className="mb-5">
      <label className="block text-gray-800 font-semibold mb-1 text-sm">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {field_type === 'text' && (
        <input
          type="text"
          className={baseClass}
          value={value || ''}
          onChange={handleChange}
          disabled={disabled}
          maxLength={validation_rules?.max_length}
          data-field-id={field_id}
        />
      )}

      {field_type === 'number' && (
        <input
          type="number"
          className={baseClass}
          value={value || ''}
          onChange={handleChange}
          disabled={disabled}
          min={validation_rules?.min}
          max={validation_rules?.max}
          data-field-id={field_id}
        />
      )}

      {field_type === 'date' && (
        <input
          type="date"
          className={baseClass}
          value={value ? (typeof value === 'string' ? value.split('T')[0] : value) : ''}
          onChange={handleChange}
          disabled={disabled}
          data-field-id={field_id}
        />
      )}

      {field_type === 'select' && (
        <select
          className={baseClass}
          value={value || ''}
          onChange={handleChange}
          disabled={disabled}
          data-field-id={field_id}
        >
          <option value="">— Select an option —</option>
          {(options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}

      {field_type === 'textarea' && (
        <textarea
          className={`${baseClass} resize-vertical`}
          rows={5}
          value={value || ''}
          onChange={handleChange}
          disabled={disabled}
          maxLength={validation_rules?.max_length}
          data-field-id={field_id}
        />
      )}

      {help_text && (
        <p className="text-gray-500 text-xs mt-1 flex items-start gap-1">
          <i className="fas fa-info-circle mt-0.5 flex-shrink-0"></i>
          {help_text}
        </p>
      )}

      {field_type === 'textarea' && validation_rules?.max_length && (
        <div className="text-right text-xs text-gray-400 mt-1">
          {(value || '').length} / {validation_rules.max_length}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step Form — renders all fields for a step
// ---------------------------------------------------------------------------
const StepForm = ({ step, stepData, onChange, disabled }) => {
  const { fields = [], step_name, help_text } = step;

  // Evaluate conditional logic for a field
  const isVisible = (field) => {
    const logic = field.conditional_logic;
    if (!logic?.show_if) return true;
    const { field_id: condFieldId, value: condValue } = logic.show_if;
    return stepData?.[condFieldId] === condValue;
  };

  return (
    <div>
      {help_text && (
        <div className="bg-blue-50 border-l-4 border-blue-400 px-4 py-3 mb-6 rounded-r-lg">
          <p className="text-blue-800 text-sm">{help_text}</p>
        </div>
      )}

      {fields.length === 0 ? (
        <div className="text-center py-8">
          <i className="fas fa-check-circle text-green-500 text-5xl mb-4"></i>
          <p className="text-gray-600 text-lg font-medium">You're ready to submit!</p>
          <p className="text-gray-500 text-sm mt-2">
            Review your answers in the summary below, then click "Submit Workflow".
          </p>
        </div>
      ) : (
        fields.filter(isVisible).map((field) => (
          <FieldRenderer
            key={field.field_id}
            field={field}
            value={stepData?.[field.field_id]}
            onChange={(fieldId, val) => onChange(fieldId, val)}
            disabled={disabled}
          />
        ))
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Review Summary — step-by-step summary before submit
// ---------------------------------------------------------------------------
const ReviewSummary = ({ schema, workflowData, isSubmitted }) => {
  const steps = (schema?.steps || []).filter((s) => s.step_id !== 'review_submit');

  return (
    <div className="space-y-6">
      {steps.map((step) => {
        const data = workflowData?.steps_data?.[step.step_id] || {};
        const hasData = Object.keys(data).length > 0;
        // A step is considered done if it has data OR the whole workflow was submitted
        const isDone = hasData || isSubmitted;
        return (
          <div key={step.step_id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              {isDone ? (
                <i className="fas fa-check-circle text-green-500"></i>
              ) : (
                <i className="fas fa-circle text-gray-300"></i>
              )}
              {step.step_name}
            </h4>
            {hasData ? (
              <div className="grid sm:grid-cols-2 gap-2">
                {step.fields
                  .filter((f) => data[f.field_id] !== undefined && data[f.field_id] !== '')
                  .map((f) => (
                    <div key={f.field_id} className="text-sm">
                      <span className="text-gray-500">{f.label}: </span>
                      <span className="text-gray-800 font-medium">
                        {f.field_type === 'select'
                          ? (f.options || []).find((o) => o.value === data[f.field_id])?.label || data[f.field_id]
                          : typeof data[f.field_id] === 'string' && data[f.field_id].length > 100
                            ? data[f.field_id].substring(0, 100) + '…'
                            : String(data[f.field_id])}
                      </span>
                    </div>
                  ))}
              </div>
            ) : isSubmitted ? (
              <p className="text-gray-400 text-sm italic">Step completed</p>
            ) : (
              <p className="text-gray-400 text-sm italic">No data entered for this step</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Evidence Criteria Panel
// ---------------------------------------------------------------------------
const EvidencePanel = ({ schema }) => {
  const criteria = schema?.evidence_criteria || [];
  if (criteria.length === 0) return null;

  return (
    <div className="mt-6">
      <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
        <i className="fas fa-folder-open text-yellow-600"></i>
        Evidence Checklist
      </h3>
      <div className="space-y-3">
        {criteria.map((c) => (
          <div key={c.criterion_id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start gap-2 mb-1">
              {c.required ? (
                <i className="fas fa-asterisk text-red-500 text-xs mt-1 flex-shrink-0"></i>
              ) : (
                <i className="fas fa-circle text-yellow-400 text-xs mt-1 flex-shrink-0"></i>
              )}
              <span className="font-semibold text-gray-800 text-sm">{c.criterion_name}</span>
            </div>
            <p className="text-gray-600 text-xs ml-4 mb-2">{c.uscis_standard}</p>
            <ul className="ml-4 space-y-0.5">
              {c.qualifying_examples.map((ex, i) => (
                <li key={i} className="text-xs text-gray-500 flex items-start gap-1">
                  <i className="fas fa-chevron-right text-yellow-500 text-xs mt-0.5 flex-shrink-0"></i>
                  {ex}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-2">
        <i className="fas fa-asterisk text-red-500 mr-1"></i> = Required evidence
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main WorkflowEngine Component
// ---------------------------------------------------------------------------
const WorkflowEngine = () => {
  const { pathwayId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [schema, setSchema] = useState(null);
  const [workflowData, setWorkflowData] = useState(null);
  const [strengthRecommendations, setStrengthRecommendations] = useState([]);
  const [activeStepId, setActiveStepId] = useState(null);
  const [stepFormData, setStepFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [startError, setStartError] = useState('');
  const [showDocPanel, setShowDocPanel] = useState(false);  // Phase 5: Document panel
  const [docPanelTab, setDocPanelTab] = useState('documents');
  const [showFormPanel, setShowFormPanel] = useState(false);  // Phase 6: Form pre-population panel
  const [showCaseStatusPanel, setShowCaseStatusPanel] = useState(false);  // Phase 7: Case status tracker
  const [prefilingRisk, setPrefilingRisk] = useState(null);
  const [prefilingRiskLoading, setPrefilingRiskLoading] = useState(false);
  const [prefilingRiskError, setPrefilingRiskError] = useState('');
  const [hasEntitlement, setHasEntitlement] = useState(null);
  const pathwayMeta = getPathway(pathwayId);
  const {
    beginPurchase,
    modal: checkoutModal,
    error: purchaseError,
  } = usePathwayPurchase({
    onNeedAuth: () => { window.location.href = '/?auth=1&purchase=1'; },
  });

  // ---------------------------------------------------------------------------
  // Load schema + workflow on mount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!pathwayId) return;
    if (getPathway(pathwayId)) setPendingPurchase(pathwayId);
    loadSchemaAndWorkflow();
    // eslint-disable-next-line
  }, [pathwayId]);

  useEffect(() => {
    if (!pathwayId) return;
    const token = localStorage.getItem('token');
    if (!token) {
      setHasEntitlement(false);
      return;
    }
    let cancelled = false;
    fetch(`${API}/api/me/entitlement`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) setHasEntitlement((data?.active_pathways || []).includes(pathwayId));
      })
      .catch(() => {
        if (!cancelled) setHasEntitlement(false);
      });
    return () => { cancelled = true; };
  }, [pathwayId]);

  const refreshStrength = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/workflow/${pathwayId}/strength`);
      setStrengthRecommendations(data.recommendations || []);
    } catch {
      /* strength optional until workflow exists */
    }
  }, [pathwayId]);

  const loadSchemaAndWorkflow = async () => {
    setLoading(true);
    setError('');
    try {
      // Load schema (public)
      const schemaData = await apiFetch(`/api/workflow/schema/${pathwayId}`);
      setSchema(schemaData);

      // Try to load existing workflow
      try {
        const wf = await apiFetch(`/api/workflow/${pathwayId}`);
        setWorkflowData(wf);
        // Set active step to current_step
        setActiveStepId(wf.current_step || schemaData.steps?.[0]?.step_id);
        // Pre-populate step form data from existing steps_data
        if (wf.steps_data && wf.steps_data[wf.current_step]) {
          setStepFormData(wf.steps_data[wf.current_step]);
        }
        await refreshStrength();
      } catch (wfErr) {
        // No workflow yet — that's fine, user will start one
        if (wfErr.status !== 404) {
          console.error('Workflow load error:', wfErr);
        }
        setActiveStepId(schemaData.steps?.[0]?.step_id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Start workflow
  // ---------------------------------------------------------------------------
  const handleStart = async () => {
    setStartError('');
    setSaving(true);
    try {
      const wf = await apiFetch(`/api/workflow/${pathwayId}`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setWorkflowData(wf);
      setActiveStepId(wf.current_step || schema?.steps?.[0]?.step_id);
      if (wf.steps_data?.[wf.current_step]) {
        setStepFormData(wf.steps_data[wf.current_step]);
      }
    } catch (err) {
      if (err.status === 403) {
        setStartError(
          `You need to purchase the ${pathwayMeta?.name || pathwayId} pathway to start this workflow.`
        );
      } else {
        setStartError(err.message || 'Failed to start workflow');
      }
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Navigate to a step
  // ---------------------------------------------------------------------------
  const goToStep = useCallback(
    (stepId) => {
      if (activeStepId === stepId) return;
      // Save current step form data before switching
      setActiveStepId(stepId);
      const existingData = workflowData?.steps_data?.[stepId] || {};
      setStepFormData(existingData);
      setError('');
      setSuccessMsg('');
    },
    [activeStepId, workflowData]
  );

  // ---------------------------------------------------------------------------
  // Field change handler
  // ---------------------------------------------------------------------------
  const handleFieldChange = useCallback((fieldId, value) => {
    setStepFormData((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  // ---------------------------------------------------------------------------
  // Save current step
  // ---------------------------------------------------------------------------
  const handleSaveStep = async () => {
    if (!activeStepId || activeStepId === 'review_submit') return;
    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      const updated = await apiFetch(`/api/workflow/${pathwayId}/step/${activeStepId}`, {
        method: 'PATCH',
        body: JSON.stringify({ step_data: stepFormData }),
      });
      setWorkflowData(updated);
      await refreshStrength();
      setSuccessMsg('Step saved!');
      setTimeout(() => setSuccessMsg(''), 2500);
    } catch (err) {
      setError(err.message || 'Failed to save step');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Save + advance to next step
  // ---------------------------------------------------------------------------
  const handleSaveAndNext = async () => {
    if (!activeStepId) return;
    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      let updated = workflowData;
      if (activeStepId !== 'review_submit') {
        updated = await apiFetch(`/api/workflow/${pathwayId}/step/${activeStepId}`, {
          method: 'PATCH',
          body: JSON.stringify({ step_data: stepFormData }),
        });
        setWorkflowData(updated);
        await refreshStrength();
      }

      // Move to next step
      const steps = schema?.steps || [];
      const sorted = [...steps].sort((a, b) => a.step_order - b.step_order);
      const idx = sorted.findIndex((s) => s.step_id === activeStepId);
      if (idx >= 0 && idx + 1 < sorted.length) {
        const nextStepId = sorted[idx + 1].step_id;
        setActiveStepId(nextStepId);
        setStepFormData(updated?.steps_data?.[nextStepId] || {});
      }
      setSuccessMsg('Step saved! Moving to next step…');
      setTimeout(() => setSuccessMsg(''), 2000);
    } catch (err) {
      setError(err.message || 'Failed to save step');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Submit workflow
  // ---------------------------------------------------------------------------
  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const submitted = await apiFetch(`/api/workflow/${pathwayId}/submit`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setWorkflowData(submitted);
      setSuccessMsg('🎉 Workflow submitted successfully!');
      // Phase 5: Auto-open document panel after submission
      setTimeout(() => setShowDocPanel(true), 1200);
    } catch (err) {
      if (err.data?.code === 'VALIDATION_ERRORS') {
        setError(`Please complete required fields before submitting:\n${err.message}`);
      } else {
        setError(err.message || 'Failed to submit workflow');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Pre-filing RFE risk (AC 47) — fetch on review/submit step
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!pathwayId || !workflowData?.id) return;
    if (activeStepId !== 'review_submit') return;
    if (workflowData.status === 'submitted') return;

    let cancelled = false;
    const load = async () => {
      setPrefilingRiskLoading(true);
      setPrefilingRiskError('');
      try {
        const qs = workflowData.id
          ? `?workflow_id=${encodeURIComponent(workflowData.id)}`
          : '';
        const data = await apiFetch(`/api/rfe/prefiling-risk/${pathwayId}${qs}`);
        if (!cancelled) setPrefilingRisk(data);
      } catch (err) {
        if (!cancelled) {
          setPrefilingRisk(null);
          setPrefilingRiskError(err.message || 'Could not load pre-filing risk.');
        }
      } finally {
        if (!cancelled) setPrefilingRiskLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [pathwayId, activeStepId, workflowData?.id, workflowData?.status]);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  const isLocked = workflowData?.status === 'submitted';
  const activeStep = schema?.steps?.find((s) => s.step_id === activeStepId);
  const isReviewStep = activeStepId === 'review_submit';

  const stepCompleted = (stepId) => {
    if (!workflowData?.steps_data) return false;
    const data = workflowData.steps_data[stepId];
    return data && Object.keys(data).length > 0;
  };

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-yellow-600 text-4xl mb-4"></i>
          <p className="text-gray-600">Loading workflow…</p>
        </div>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <i className="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Workflow Not Found</h2>
          <p className="text-gray-600 mb-4">{error || `No workflow schema available for pathway "${pathwayId}".`}</p>
          <button onClick={() => navigate('/dashboard')} className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 transition-colors">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // No workflow started yet
  // ---------------------------------------------------------------------------
  if (!workflowData) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Nav */}
        <nav className="bg-[#303035] border-b border-yellow-600/20 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white transition-colors">
              <i className="fas fa-arrow-left"></i>
            </button>
            <div className="w-7 h-7 bg-yellow-600 rounded-full flex items-center justify-center">
              <i className="fas fa-balance-scale text-black text-xs"></i>
            </div>
            <span className="text-white font-bold font-serif">ImmigrateSmart</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-sm hidden sm:inline">{user?.full_name || user?.email}</span>
            <PurchaseNavButton />
          </div>
        </nav>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          {/* Pathway header */}
          <div className="bg-white rounded-2xl shadow-xl p-10 border border-gray-100">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="fas fa-route text-yellow-600 text-2xl"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3 font-serif">{schema.pathway_name}</h1>
            <p className="text-gray-600 mb-6 max-w-lg mx-auto">
              This guided workflow will take you through {schema.steps?.length} steps to complete
              your immigration application with confidence.
            </p>

            {/* Workflow overview */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-yellow-600">{schema.steps?.length || 0}</div>
                <div className="text-sm text-gray-600">Steps</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-yellow-600">
                  {schema.evidence_criteria?.length || 0}
                </div>
                <div className="text-sm text-gray-600">Evidence Types</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-yellow-600">Free</div>
                <div className="text-sm text-gray-600">Auto-populated from profile</div>
              </div>
            </div>

            {/* Step overview */}
            <div className="text-left mb-8 space-y-2">
              {schema.steps?.map((step, idx) => (
                <div key={step.step_id} className="flex items-center gap-3 py-2 border-b border-gray-100">
                  <span className="w-7 h-7 rounded-full bg-yellow-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {idx + 1}
                  </span>
                  <div>
                    <div className="font-medium text-gray-800 text-sm">{step.step_name}</div>
                    {step.help_text && (
                      <div className="text-xs text-gray-500 truncate max-w-sm">{step.help_text}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {startError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-start gap-2">
                <i className="fas fa-lock mt-0.5 flex-shrink-0"></i>
                <div>{startError}</div>
              </div>
            )}
            {purchaseError && (
              <p className="text-red-600 text-sm mb-4">{purchaseError}</p>
            )}

            {hasEntitlement === false ? (
              <>
                <button
                  type="button"
                  data-testid="workflow-purchase-cta"
                  onClick={() => beginPurchase(pathwayId)}
                  className="purchase-cta-btn w-full sm:w-auto px-10 py-4 text-base shadow-lg"
                >
                  Purchase {pathwayMeta?.name || 'this pathway'} — {pathwayMeta?.price || ''}
                </button>
                <p className="text-gray-500 text-sm mt-4">
                  Payment unlocks this workflow, generated documents, and evidence tools.
                </p>
              </>
            ) : (
              <button
                onClick={handleStart}
                disabled={saving}
                className="bg-yellow-600 text-white px-10 py-4 rounded-xl text-lg font-semibold hover:bg-yellow-700 transition-colors disabled:opacity-50 shadow-lg"
              >
                {saving ? (
                  <><i className="fas fa-spinner fa-spin mr-2"></i>Starting…</>
                ) : (
                  <><i className="fas fa-play mr-2"></i>Start Workflow</>
                )}
              </button>
            )}

            <p className="text-gray-400 text-xs mt-4">
              Your progress is saved automatically at each step.
            </p>
          </div>
        </div>
        {checkoutModal}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Main workflow UI
  // ---------------------------------------------------------------------------
  const sortedSteps = [...(schema.steps || [])].sort((a, b) => a.step_order - b.step_order);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-[#303035] border-b border-yellow-600/20 px-6 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white transition-colors text-sm">
            <i className="fas fa-arrow-left mr-1"></i>
            Dashboard
          </button>
          <span className="text-gray-600">|</span>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-yellow-600 rounded-full flex items-center justify-center">
              <i className="fas fa-balance-scale text-black text-xs"></i>
            </div>
            <span className="text-white font-semibold text-sm font-serif truncate max-w-48">{schema.pathway_name}</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {!isLocked && (
            <button
              onClick={() => { setDocPanelTab('review'); setShowDocPanel(true); }}
              className="bg-yellow-500 text-black text-xs px-3 py-1.5 rounded-lg hover:bg-yellow-400 transition-colors font-semibold hidden sm:flex items-center gap-1.5"
              title="Purchase or manage Attorney Draft Review"
            >
              <i className="fas fa-gavel"></i> Attorney Review <span className="opacity-80 font-normal">(soon)</span>
            </button>
          )}
          {!isLocked && (
            <button
              onClick={() => { setDocPanelTab('evidence_builder'); setShowDocPanel(true); }}
              className="bg-white/10 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-white/20 transition-colors font-semibold hidden sm:flex items-center gap-1.5"
              title="Evidence Builder"
            >
              <i className="fas fa-folder-open"></i> Evidence
            </button>
          )}
          {isLocked && (
            <>
              <span className="bg-green-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                <i className="fas fa-check mr-1"></i>Submitted
              </span>
              {/* Phase 5: Documents shortcut in nav */}
              <button
                onClick={() => { setDocPanelTab('documents'); setShowDocPanel(true); }}
                className="bg-yellow-500 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-yellow-600 transition-colors font-semibold hidden sm:flex items-center gap-1.5"
              >
                <i className="fas fa-file-export"></i> Documents
              </button>
              {/* Phase 6: Form pre-population shortcut in nav */}
              <button
                onClick={() => setShowFormPanel(true)}
                className="bg-red-600 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-red-700 transition-colors font-semibold hidden sm:flex items-center gap-1.5"
                title="USCIS Form Pre-Population & PDF Export"
              >
                <i className="fas fa-file-pdf"></i> Form Export
              </button>
              {/* Phase 7: Case status tracker shortcut in nav */}
              <button
                onClick={() => setShowCaseStatusPanel(true)}
                className="text-xs px-3 py-1.5 rounded-lg transition-colors font-semibold hidden sm:flex items-center gap-1.5"
                style={{ backgroundColor: '#C6A664', color: '#1B1B1E' }}
                title="Case Status Tracking & USCIS Receipt Number"
              >
                <i className="fas fa-chart-line"></i> Case Status
              </button>
            </>
          )}
          <span className="text-gray-400 text-sm hidden sm:block">{user?.full_name || user?.email}</span>
          <PurchaseNavButton />
        </div>
      </nav>

      {/* Progress bar */}
      <div className="bg-[#3A3A3F] h-1">
        <div
          className="h-1 bg-yellow-600 transition-all duration-700"
          style={{ width: `${workflowData.completion_percentage || 0}%` }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6">
        {/* Left Sidebar — Step Navigator */}
        <aside className="w-64 flex-shrink-0 hidden md:block">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sticky top-20">
            {/* Case Strength */}
            <div className="mb-5">
              <CaseStrengthMeter
                score={workflowData.case_strength_score}
                level={workflowData.case_strength_level}
                recommendations={strengthRecommendations}
              />
            </div>

            {/* Progress */}
            <div className="flex justify-between text-xs text-gray-500 mb-4">
              <span>{workflowData.completed_steps} of {workflowData.total_steps - 1} steps done</span>
              <span>{workflowData.completion_percentage}%</span>
            </div>

            {/* Step list */}
            <nav className="space-y-1">
              {sortedSteps.map((step, idx) => {
                const isActive = activeStepId === step.step_id;
                const isDone =
                  // If whole workflow is submitted, every step is done
                  isLocked
                  // Otherwise check individual step data
                  || (stepCompleted(step.step_id) && step.step_id !== 'review_submit');

                return (
                  <button
                    key={step.step_id}
                    onClick={() => goToStep(step.step_id)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2.5 transition-all text-sm ${isActive
                      ? 'bg-yellow-600 text-white font-semibold shadow-sm'
                      : isDone
                        ? 'text-green-700 hover:bg-green-50'
                        : 'text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0 font-bold ${isActive ? 'bg-white text-yellow-600' : isDone ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                      }`}>
                      {isDone && !isActive ? <i className="fas fa-check text-xs"></i> : idx + 1}
                    </span>
                    <span className="truncate">{step.step_name}</span>
                  </button>
                );
              })}
            </nav>

            {/* Evidence panel (collapsed by default) */}
            <details className="mt-4 text-xs text-gray-500 cursor-pointer">
              <summary className="font-semibold text-gray-700 py-2 flex items-center gap-1">
                <i className="fas fa-folder text-yellow-500"></i> Evidence Checklist
              </summary>
              <EvidencePanel schema={schema} />
            </details>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          {/* Step header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-yellow-700 font-bold text-sm">
                  {sortedSteps.findIndex((s) => s.step_id === activeStepId) + 1}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{activeStep?.step_name || 'Step'}</h2>
                <p className="text-gray-500 text-sm">
                  Step {sortedSteps.findIndex((s) => s.step_id === activeStepId) + 1} of {sortedSteps.length}
                  {isLocked && <span className="ml-2 text-green-600 font-semibold"><i className="fas fa-lock mr-1"></i>Submitted</span>}
                </p>
              </div>
            </div>
          </div>

          {/* Notifications */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-start gap-2">
              <i className="fas fa-exclamation-circle mt-0.5"></i>
              <span style={{ whiteSpace: 'pre-wrap' }}>{error}</span>
            </div>
          )}
          {successMsg && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-center gap-2">
              <i className="fas fa-check-circle"></i>
              {successMsg}
            </div>
          )}

          {/* SUBMITTED state */}
          {isLocked && !isReviewStep ? (
            <div className="bg-white rounded-xl shadow-sm border border-green-200 p-8 text-center">
              <i className="fas fa-check-circle text-green-500 text-5xl mb-4"></i>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Workflow Submitted!</h3>
              <p className="text-gray-600 mb-4">
                Your {schema.pathway_name} workflow has been submitted successfully.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 inline-block mb-4">
                <CaseStrengthMeter
                  score={workflowData.case_strength_score}
                  level={workflowData.case_strength_level}
                  recommendations={strengthRecommendations}
                />
              </div>
              {/* Phase 5: Document Generation CTA */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📄</span>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Your documents are ready</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Petition draft, filing checklist, cover letter &amp; evidence summary have been generated.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 justify-center flex-wrap">
                <button onClick={() => goToStep('review_submit')} className="bg-gray-100 text-gray-700 px-5 py-2.5 rounded-lg hover:bg-gray-200 transition-colors text-sm font-semibold">
                  <i className="fas fa-eye mr-2"></i>View Summary
                </button>
                {/* Phase 5: Open document panel */}
                <button
                  onClick={() => setShowDocPanel(true)}
                  className="bg-yellow-500 text-white px-5 py-2.5 rounded-lg hover:bg-yellow-600 transition-colors text-sm font-semibold"
                >
                  <i className="fas fa-file-export mr-2"></i>Documents &amp; Review
                </button>
                {/* Phase 7: Case status tracker CTA */}
                <button
                  onClick={() => setShowCaseStatusPanel(true)}
                  className="px-5 py-2.5 rounded-lg transition-colors text-sm font-semibold"
                  style={{ backgroundColor: '#C6A664', color: '#1B1B1E' }}
                >
                  <i className="fas fa-chart-line mr-2"></i>Track Case Status
                </button>
                <button onClick={() => navigate('/dashboard')} className="bg-[#3A3A3F] text-white px-5 py-2.5 rounded-lg hover:bg-gray-800 transition-colors text-sm font-semibold">
                  <i className="fas fa-home mr-2"></i>Back to Dashboard
                </button>
              </div>
            </div>
          ) : (
            /* Step Form */
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              {isReviewStep ? (
                <>
                  <ReviewSummary schema={schema} workflowData={workflowData} isSubmitted={isLocked} />
                  {!isLocked && (
                    <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <i className="fas fa-shield-alt text-amber-600 mt-0.5"></i>
                        <div>
                          <p className="font-semibold text-gray-800 text-sm">Pre-filing RFE risk check</p>
                          <p className="text-xs text-gray-600 mt-0.5">
                            Review common RFE triggers for this pathway before you submit.
                          </p>
                        </div>
                      </div>
                      {prefilingRiskLoading && (
                        <p className="text-xs text-gray-500">
                          <i className="fas fa-spinner fa-spin mr-1"></i>Assessing risk areas…
                        </p>
                      )}
                      {prefilingRiskError && (
                        <p className="text-xs text-amber-800">{prefilingRiskError}</p>
                      )}
                      {prefilingRisk && !prefilingRiskLoading && (
                        <div className="space-y-3">
                          <p className="text-xs font-medium text-gray-700">
                            Overall level:{' '}
                            <span className="uppercase tracking-wide">
                              {prefilingRisk.overall_level}
                            </span>
                          </p>
                          <ul className="space-y-2">
                            {(prefilingRisk.risk_areas || []).map((area, idx) => (
                              <li
                                key={`${area.area}-${idx}`}
                                className="bg-white/80 border border-amber-100 rounded-lg p-3"
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <span
                                    className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                                      area.severity === 'high'
                                        ? 'bg-red-100 text-red-700'
                                        : area.severity === 'medium'
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : 'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    {area.severity}
                                  </span>
                                  <span className="text-sm font-semibold text-gray-800">{area.area}</span>
                                </div>
                                <p className="text-xs text-gray-600 leading-relaxed">{area.explanation}</p>
                              </li>
                            ))}
                          </ul>
                          {prefilingRisk.disclaimer && (
                            <p className="text-[11px] text-gray-500 italic">{prefilingRisk.disclaimer}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                activeStep && (
                  <StepForm
                    step={activeStep}
                    stepData={stepFormData}
                    onChange={handleFieldChange}
                    disabled={isLocked}
                  />
                )
              )}

              {/* Action buttons */}
              {!isLocked && (
                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-3 flex-wrap">
                  {/* Prev */}
                  {(() => {
                    const idx = sortedSteps.findIndex((s) => s.step_id === activeStepId);
                    return idx > 0 ? (
                      <button
                        onClick={() => goToStep(sortedSteps[idx - 1].step_id)}
                        className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-semibold"
                      >
                        <i className="fas fa-arrow-left mr-2"></i>Back
                      </button>
                    ) : null;
                  })()}

                  {isReviewStep ? (
                    /* Submit button */
                    <button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="ml-auto px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold shadow-md disabled:opacity-50"
                    >
                      {submitting ? (
                        <><i className="fas fa-spinner fa-spin mr-2"></i>Submitting…</>
                      ) : (
                        <><i className="fas fa-paper-plane mr-2"></i>Submit Workflow</>
                      )}
                    </button>
                  ) : (
                    <>
                      {/* Save */}
                      <button
                        onClick={handleSaveStep}
                        disabled={saving}
                        className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-semibold disabled:opacity-50"
                      >
                        {saving ? <i className="fas fa-spinner fa-spin"></i> : <><i className="fas fa-save mr-1"></i>Save</>}
                      </button>

                      {/* Save & Next */}
                      {(() => {
                        const idx = sortedSteps.findIndex((s) => s.step_id === activeStepId);
                        const isLast = idx === sortedSteps.length - 1;
                        return (
                          <button
                            onClick={handleSaveAndNext}
                            disabled={saving}
                            className="ml-auto px-6 py-2.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-semibold disabled:opacity-50 shadow-sm"
                          >
                            {saving ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : isLast ? (
                              <><i className="fas fa-check mr-2"></i>Save & Review</>
                            ) : (
                              <>Save & Next <i className="fas fa-arrow-right ml-2"></i></>
                            )}
                          </button>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Mobile step navigation */}
          <div className="md:hidden mt-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3 text-sm">Steps</h3>
            <div className="flex flex-wrap gap-2">
              {sortedSteps.map((step, idx) => (
                <button
                  key={step.step_id}
                  onClick={() => goToStep(step.step_id)}
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${activeStepId === step.step_id
                    ? 'bg-yellow-600 text-white shadow-md'
                    : (isLocked || stepCompleted(step.step_id))
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                    }`}
                  title={step.step_name}
                >
                  {(isLocked || stepCompleted(step.step_id)) && activeStepId !== step.step_id ? (
                    <i className="fas fa-check text-xs"></i>
                  ) : idx + 1}
                </button>
              ))}
            </div>
          </div>
        </main>
      </div>

      {/* Phase 5: Document Panel Modal */}
      {showDocPanel && (
        <DocumentPanel
          pathwayId={pathwayId}
          workflowStatus={workflowData?.status}
          initialTab={docPanelTab}
          onClose={() => setShowDocPanel(false)}
        />
      )}

      {/* Phase 6: Form Export Panel Modal */}
      {showFormPanel && (
        <FormExportPanel
          pathwayId={pathwayId}
          workflowStatus={workflowData?.status}
          onClose={() => setShowFormPanel(false)}
        />
      )}

      {/* Phase 7: Case Status Panel Modal */}
      {showCaseStatusPanel && (
        <CaseStatusPanel
          pathwayId={pathwayId}
          onClose={() => setShowCaseStatusPanel(false)}
        />
      )}
      {checkoutModal}
    </div>
  );
};

export default WorkflowEngine;
